import { useRef } from 'react';
import type { ChangeEvent } from 'react';
import type { SimulationControls, SimulationUIState } from '../hooks/useSimulationWorker';
import './SaveMenu.css';

interface SaveMenuProps {
  state: SimulationUIState;
  controls: SimulationControls;
}

const NEW_GAME_CONFIRMATION = 'Start a new game? The current game is lost unless it is saved. You will choose a base again.';

export function SaveMenu({ state, controls }: SaveMenuProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const notReady = !state.ready;

  const onFileChosen = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    // Clear the input so choosing the same file again still fires a change.
    event.target.value = '';
    if (file !== undefined) controls.importSave(file);
  };

  const onNewGame = (): void => {
    if (window.confirm(NEW_GAME_CONFIRMATION)) controls.resetToBasePicker();
  };

  const autosaveText =
    state.lastAutosaveDay === null ? 'Not autosaved yet' : `Autosaved at day ${state.lastAutosaveDay}`;

  return (
    <div className="save-menu" role="group" aria-label="Save and load">
      <button type="button" className="btn btn-small" onClick={controls.saveGame} disabled={notReady}>
        Save Game
      </button>
      <button type="button" className="btn btn-small" onClick={controls.loadGame} disabled={!state.worldLoaded}>
        Load Game
      </button>
      <button type="button" className="btn btn-small" onClick={controls.exportSave} disabled={notReady}>
        Export Save (.json)
      </button>
      <button type="button" className="btn btn-small" onClick={() => fileInput.current?.click()} disabled={!state.worldLoaded}>
        Import Save (.json)
      </button>
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        className="visually-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={onFileChosen}
      />
      <button type="button" className="btn btn-small btn-danger" onClick={onNewGame} disabled={notReady}>
        New Game (Reset)
      </button>
      <p className="save-notice" role="status">
        <span className="muted">{autosaveText}</span>
        {state.saveNotice !== null && <span className="save-message"> · {state.saveNotice}</span>}
      </p>
    </div>
  );
}
